import * as Sentry from '@sentry/nextjs'
import type { Product } from 'cf-grpc/creativefabrica/content/v2/types_pb'
import { LANGUAGE_COOKIE_NAME } from 'constants/cookies'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { marketplaceListProductsQueryFetcher } from '@web/gen/content'
import { Language, MarketplaceLanguage, MarketplaceProductSortOrder } from '@web/gen/types'
import type { LocalizedStrings } from 'codegen/types'
import type { SortType } from '../legacy-product-category-page/pagination/sort'

type MarketplaceListProductsResponse = Awaited<
  ReturnType<ReturnType<typeof marketplaceListProductsQueryFetcher>>
>

export type MarketplaceProductsPayload = NonNullable<
  MarketplaceListProductsResponse['marketplaceListProducts']
>
export type MarketplaceProduct = NonNullable<MarketplaceProductsPayload['products']>[number]
type RelatedCategories = NonNullable<
  NonNullable<MarketplaceProductsPayload['categoryPageData']>['relatedCategories']
>
type RelatedCategory = RelatedCategories[number]

export interface MarketplaceCategoryProduct {
  productId?: number | null
  productUrl?: string | null
  productImage?: string | null
  imageAlt?: string | null
  productTitle?: string | null
  categoryUrl?: string | null
  productTitleLocalized?: LocalizedStrings | null
  cardCategory?: LocalizedStrings | null
  productCountdown?: string | null
}

function getHeaders(locale: string) {
  return new Headers({
    Cookie: `${LANGUAGE_COOKIE_NAME}=${locale}`,
  })
}

export async function getMarketplaceProducts(params: {
  locale: string
  slug: string
  limit: number
  page?: number
  orderBy?: MarketplaceProductSortOrder
}) {
  const fetcher = marketplaceListProductsQueryFetcher(
    {
      input: {
        categorySlug: params.slug,
        locale: mapLocaleToMarketplaceLanguage(params.locale),
        pagination: {
          currentPage: params.page ?? 1,
          pageSize: params.limit,
        },
        sortType: params.orderBy ?? MarketplaceProductSortOrder.ProductSortOrderMostPopular,
      },
    },
    {
      headers: getHeaders(params.locale),
    },
  )

  let response: MarketplaceListProductsResponse
  try {
    response = await fetcher()
  } catch (error) {
    Sentry.captureException(error)
    throw error
  }

  const responseErrors = response.marketplaceListProducts?.errors
  if (responseErrors && responseErrors.length > 0) {
    const error = new Error(
      `marketplaceListProducts returned errors: ${responseErrors
        .map(e => `[${e.code}] ${e.message}`)
        .join('; ')}`,
    )
    Sentry.captureException(error, {
      extra: {
        input: {
          categorySlug: params.slug,
          locale: params.locale,
          page: params.page ?? 1,
          limit: params.limit,
          orderBy: params.orderBy,
        },
        errors: responseErrors,
      },
    })
    throw error
  }

  return response
}

export function setSortingOptions(orderBy: SortType): MarketplaceProductSortOrder {
  switch (orderBy) {
    case 'date': {
      return MarketplaceProductSortOrder.ProductSortOrderMostRecent
    }
    default: {
      return MarketplaceProductSortOrder.ProductSortOrderMostPopular
    }
  }
}

function mapLocaleToMarketplaceLanguage(locale: string): MarketplaceLanguage {
  switch (locale) {
    case Language.De: {
      return MarketplaceLanguage.LanguageDe
    }
    case Language.Es: {
      return MarketplaceLanguage.LanguageEs
    }
    case Language.Fr: {
      return MarketplaceLanguage.LanguageFr
    }
    case Language.It: {
      return MarketplaceLanguage.LanguageIt
    }
    case Language.Nl: {
      return MarketplaceLanguage.LanguageNl
    }
    case Language.Pl: {
      return MarketplaceLanguage.LanguagePl
    }
    case Language.Pt: {
      return MarketplaceLanguage.LanguagePt
    }
    default: {
      return MarketplaceLanguage.LanguageEn
    }
  }
}

function getSlugFromProductUrl(productUrl?: string | null) {
  if (!productUrl) {
    return ''
  }

  const normalized = productUrl.trim()
  const withBase = normalized.startsWith('http')
    ? normalized
    : `https://creativefabrica.com${normalized}`

  try {
    const { pathname } = new URL(withBase)
    const segments = pathname.split('/').filter(Boolean)

    const productSegmentIndex = segments.findIndex(segment => segment === 'product')
    if (productSegmentIndex >= 0) {
      const slugAfterProduct = segments[productSegmentIndex + 1]
      if (slugAfterProduct) {
        return decodeURIComponent(slugAfterProduct)
      }
    }

    const lastPathSegment = segments.at(-1)
    return lastPathSegment ? decodeURIComponent(lastPathSegment) : ''
  } catch {
    const fallbackMatch = normalized.match(/\/product\/([^/?#]+)/)
    if (fallbackMatch?.[1]) {
      return decodeURIComponent(fallbackMatch[1])
    }

    const fallbackSegments = normalized.split('/').filter(Boolean)
    const fallbackLastSegment = fallbackSegments.at(-1)
    return fallbackLastSegment ? decodeURIComponent(fallbackLastSegment.split('?')[0] || '') : ''
  }
}

function getCategoryLabel(cardCategory: unknown, locale: string) {
  if (typeof cardCategory === 'string') {
    return cardCategory
  }

  if (typeof cardCategory === 'object' && cardCategory !== null) {
    const localized = (cardCategory as Record<string, unknown>)[locale]
    if (typeof localized === 'string') {
      return localized
    }
  }

  return ''
}

function formatCategoryLabelFromSlug(slug: string) {
  return slug
    .split('-')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .replace(/\b3d\b/gi, '3D')
}

function getCategorySlug(product: MarketplaceProduct) {
  return product.cardCategory?.trim?.()?.toLowerCase?.() ?? ''
}

function getCategoryName(product: MarketplaceProduct, locale: string) {
  const mappedCategory = getCategoryLabel(product.cardCategory, locale)
  if (mappedCategory.trim().length > 0) {
    return formatCategoryLabelFromSlug(mappedCategory.trim())
  }

  const slug = getCategorySlug(product)
  if (slug) {
    return formatCategoryLabelFromSlug(slug)
  }

  return ''
}

function getImageUrl(product: MarketplaceProduct) {
  return (
    product.mainImageSizes?.woocommerceSingle?.url ||
    product.mainImageSizes?.woocommerceThumbnail?.url ||
    product.mainImageSizes?.shopSingle?.url ||
    product.mainImageSizes?.mainImage?.url ||
    product.productImage ||
    ''
  )
}

export function normalizeMarketplaceProductUrl(productUrl?: string | null) {
  if (!productUrl) {
    return ''
  }

  const normalized = productUrl.trim()
  const withBase = normalized.startsWith('http')
    ? normalized
    : `https://creativefabrica.com${normalized.startsWith('/') ? normalized : `/${normalized}`}`

  try {
    const { pathname } = new URL(withBase)
    const segments = pathname.split('/').filter(Boolean)
    const productSegmentIndex = segments.findIndex(segment => segment === 'product')

    if (productSegmentIndex >= 0) {
      const productSlug = segments[productSegmentIndex + 1]
      return productSlug ? `${AppRoutes.PRODUCT}${productSlug}/` : ''
    }

    const lastPathSegment = segments.at(-1)
    return lastPathSegment ? `${AppRoutes.PRODUCT}${lastPathSegment}/` : ''
  } catch {
    const fallbackMatch = normalized.match(/\/product\/([^/?#]+)/)
    if (fallbackMatch?.[1]) {
      return `${AppRoutes.PRODUCT}${fallbackMatch[1]}/`
    }

    const fallbackSegments = normalized.split('/').filter(Boolean)
    const lastPathSegment = fallbackSegments.at(-1)?.split('?')[0]
    return lastPathSegment ? `${AppRoutes.PRODUCT}${lastPathSegment}/` : ''
  }
}

function mapMarketplaceProductToCategoryProduct(
  product: MarketplaceProduct,
  locale: string,
  categoryUrlPrefix = AppRoutes.SUBSCRIPTIONS_GRAPHICS,
): MarketplaceCategoryProduct {
  const categorySlug = getCategorySlug(product)
  const categoryName = getCategoryName(product, locale)

  return {
    productId: Number(product.legacyProductId || product.productId || 0),
    productUrl: normalizeMarketplaceProductUrl(product.productUrl),
    productImage: getImageUrl(product),
    imageAlt: product.productTitle || '',
    productTitle: product.productTitle || '',
    productCountdown: product.productCountdown,
    cardCategory: categoryName ? ({ [locale]: categoryName } as LocalizedStrings) : null,
    categoryUrl: categorySlug ? `${categoryUrlPrefix}${categorySlug}/` : null,
  }
}

export function mapMarketplaceProductsToCategoryProducts(
  products: MarketplaceProduct[] | null | undefined,
  locale: string,
  categoryUrlPrefix = AppRoutes.SUBSCRIPTIONS_GRAPHICS,
) {
  return (products || []).map(product =>
    mapMarketplaceProductToCategoryProduct(product, locale, categoryUrlPrefix),
  )
}

export function mapMarketplaceProductToLegacyProduct(
  product: MarketplaceProduct,
  locale: string,
): Product {
  const categorySlug = getCategorySlug(product)
  const categoryName = getCategoryName(product, locale)

  return {
    id: product.legacyProductId || product.productId || '',
    slug: getSlugFromProductUrl(product.productUrl),
    title: product.productTitle || '',
    thumbnailUrl:
      product.mainImageSizes?.woocommerceSingle?.url ||
      product.mainImageSizes?.woocommerceThumbnail?.url ||
      product.mainImageSizes?.shopSingle?.url ||
      product.mainImageSizes?.mainImage?.url ||
      product.productImage ||
      '',
    category: {
      name: categoryName,
      slug: categorySlug,
      parents: categorySlug
        ? [
            {
              name: categoryName,
              slug: categorySlug,
            },
          ]
        : [],
    },
  } as unknown as Product
}

function mapRelatedCategory(category: RelatedCategory) {
  return {
    slug: category.slug || '',
    name: category.title || '',
    children:
      category.childCategories?.map(child => ({
        slug: child.slug || '',
        name: child.title || '',
        children:
          child.childCategories?.map(grandchild => ({
            slug: grandchild.slug || '',
            name: grandchild.title || '',
            children: [],
          })) || [],
      })) || [],
  }
}

export function mapRelatedCategoriesToMenuItems(
  relatedCategories: RelatedCategories | null | undefined,
  slug: string,
  title: string,
) {
  return {
    slug,
    title,
    description: '',
    ogImageUrl: '',
    twitterImageUrl: '',
    subCategories: (relatedCategories || []).map(mapRelatedCategory),
  }
}

export function getMarketplaceProductsPayload(
  response: MarketplaceListProductsResponse | null | undefined,
) {
  return response?.marketplaceListProducts ?? null
}
